import { microServicesRoutes } from '@constants';
import { authApi, edgeApi } from '@lib/apiClients';
import {
  type CredentialsDto,
  type AuthenticationResponse,
  type AuthApiResponse,
  type EdgeApiResponse,
  type UserDataResponse,
  type LogoutResponse,
  type ApiResponse,
  type ResetPasswordDto, type AdminResetPasswordDto,
} from '@types';
import { logError } from '@lib/logger/logError';
import { extractErrorMessage } from '@lib/logger/extractErrorMessage';

export default function useAuthApi (): {
  login: (
    credentials: CredentialsDto,
  ) => Promise<ApiResponse<AuthenticationResponse>>;
  authLogin: (
    credentials: CredentialsDto,
  ) => Promise<ApiResponse<AuthenticationResponse>>;
  logout: () => Promise<ApiResponse<LogoutResponse>>;
  refresh: () => Promise<ApiResponse<AuthenticationResponse>>;
  getMe: () => Promise<ApiResponse<UserDataResponse>>;
  resetPasswordAsync: (body: ResetPasswordDto, token: string) => Promise<ApiResponse<AuthApiResponse>>;
  adminResetPasswordAsync: (payload: AdminResetPasswordDto)=> Promise<ApiResponse<AuthApiResponse>>;
  } {
  const handleError = (
    message: string,
    error: unknown,
    source: string,
  ): ApiResponse<never> => {
    const errorMessage = extractErrorMessage(error);

    void logError(
      message,
      source,
      {
        error: errorMessage,
        rawError: error,
      },
      false,
    );

    return {
      success: false,
      error: {
        message,
        raw: errorMessage,
      },
    };
  };

  const authLogin = async (
    credentials: CredentialsDto,
  ): Promise<ApiResponse<AuthenticationResponse>> => {
    try {
      const response = await authApi.post<AuthApiResponse>(
        microServicesRoutes.auth.post.auth.login,
        credentials,
      );

      const token = response.data.access_token;

      if (token == null || typeof token !== 'string') {
        return {
          success: false,
          error: { message: 'access_token not received from backend' },
        };
      }

      return { success: true, data: { access_token: token, status: 'ok' } };
    } catch (e) {
      return handleError('Error during Auth login.', e, 'Auth login');
    }
  };

  const login = async (
    credentials: CredentialsDto,
  ): Promise<ApiResponse<AuthenticationResponse>> => {
    try {
      const response = await edgeApi.post<EdgeApiResponse>(
        microServicesRoutes.edge.post.auth.login,
        credentials,
      );

      const token = response.data.authToken;

      if (token == null || typeof token !== 'string') {
        return {
          success: false,
          error: { message: 'authToken not received from backend' },
        };
      }

      return { success: true, data: { access_token: token, status: 'ok' } };
    } catch (e) {
      return handleError('Error during Edge login.', e, 'Edge login');
    }
  };

  const logout = async (): Promise<ApiResponse<LogoutResponse>> => {
    try {
      const response = await authApi.get<LogoutResponse>(
        microServicesRoutes.auth.get.auth.logout,
      );
      return { success: true, data: response.data };
    } catch (e) {
      return handleError('Error during logout.', e, 'logout');
    }
  };

  const refresh = async (): Promise<ApiResponse<AuthenticationResponse>> => {
    if (window.location.pathname === '/login') {
      return { success: false };
    }

    try {
      const response = await authApi.get<AuthApiResponse>(
        microServicesRoutes.auth.get.auth.refreshSession,
      );

      const token = response.data.access_token;

      if (token == null || typeof token !== 'string') {
        return { success: false };
      }

      return { success: true, data: { access_token: token, status: 'ok' } };
    } catch (e) {
      return handleError('Error refreshing session.', e, 'refresh');
    }
  };

  const getMe = async (): Promise<ApiResponse<UserDataResponse>> => {
    try {
      const response = await authApi.get<UserDataResponse>(
        microServicesRoutes.auth.get.auth.getMe,
      );
      return { success: true, data: response.data };
    } catch (e) {
      return handleError('Error validating session.', e, 'getMe');
    }
  };

  const resetPasswordAsync = async (
    body: ResetPasswordDto,
    token: string
  ): Promise<ApiResponse<AuthApiResponse>> => {
    try {
      const postConfig = {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      };

      const updatedPass = await authApi.post<AuthApiResponse>(
        microServicesRoutes.auth.post.auth.resetPassword,
        body,
        postConfig
      );
      return { success: true, data: updatedPass.data as Record<string, string> };
    } catch (e) {
      return handleError('Can not reset password.', e, 'resetPassword');
    }
  };

  const adminResetPasswordAsync = async (payload: AdminResetPasswordDto): Promise<ApiResponse<AuthApiResponse>> =>{
    try {
      const { adminEmail, userEmail, adminPassword: password, newPassword } = payload;

      const adminAuthenticationCheck = await authLogin({ email: adminEmail, password });

      if (adminAuthenticationCheck.data == null) {
        return handleError(
          'Could not authenticate Admin.',
          { error: adminAuthenticationCheck.error },
          adminResetPasswordAsync.name,
        );
      }
      const url = `${microServicesRoutes.auth.post.auth.adminResetPassword}`;

      const resetPassBody = { email: userEmail, new_password: newPassword };
      const postConfig = {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      };

      const { data } = await authApi.post<AuthApiResponse>(url, resetPassBody, postConfig);
      
      return { data, success: true };
    } catch (e) {
      return handleError(
        'Could not reset password by Admin.',
        e,
        adminResetPasswordAsync.name,
      );
    }
  };

  return { login, authLogin, logout, refresh, getMe, resetPasswordAsync, adminResetPasswordAsync };
}
